import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import {
    Building2,
    CircleX,
    Keyboard,
    Moon,
    PanelLeftClose,
    Sun,
    UserPlus,
    UsersRound
} from 'lucide-react'
import { useTheme } from '@/components/generals/theme-provider'
import { useDashboard } from '@/components/dashboard/dashboard-provider'
import { CUSTOMERS } from '@/data/customers'
import { getPlan } from '@/data/plans'
import { NAV_ITEMS } from '@/constants/navigation'

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator
} from '@/components/ui/command'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

type CommandAction = {
    id: string
    label: string
    keywords: string
    icon: LucideIcon
    run: () => void
}

const matches = (query: string, ...values: string[]): boolean => values.some((value) => value.toLowerCase().includes(query))

export const CommandMenu: FC = (): ReactNode => {
    const router = useRouter()
    const { resolvedTheme, toggleTheme } = useTheme()
    const { commandOpen, setCommandOpen, toggleCollapsed, setShortcutsOpen, collapsed } = useDashboard()
    const [query, setQuery] = useState('')
    const normalized = query.trim().toLowerCase()

    const close = (): void => {
        setCommandOpen(false)
        setQuery('')
    }

    const run = (action: () => void): void => {
        close()
        action()
    }

    const actions: CommandAction[] = [
        { id: 'add-customer', label: 'Tambah pelanggan baru', keywords: 'customer akun baru', icon: UserPlus, run: () => router.push('/pelanggan?tambah=1') },
        { id: 'invite', label: 'Undang anggota tim', keywords: 'team invite akses', icon: UsersRound, run: () => router.push('/tim?undang=1') },
        { id: 'failed', label: 'Lihat pembayaran gagal', keywords: 'tagihan faktur gagal invoice', icon: CircleX, run: () => router.push('/langganan?status=failed') },
        { id: 'theme', label: resolvedTheme === 'dark' ? 'Ganti ke mode terang' : 'Ganti ke mode gelap', keywords: 'tema theme gelap terang dark light', icon: resolvedTheme === 'dark' ? Sun : Moon, run: toggleTheme },
        { id: 'sidebar', label: collapsed ? 'Lebarkan sidebar' : 'Ciutkan sidebar', keywords: 'sidebar menu navigasi', icon: PanelLeftClose, run: toggleCollapsed },
        { id: 'shortcuts', label: 'Lihat pintasan keyboard', keywords: 'shortcut keyboard bantuan', icon: Keyboard, run: () => setShortcutsOpen(true) }
    ]

    const pages = NAV_ITEMS.filter((item) => !normalized || matches(normalized, item.label, item.description, ...item.keywords))
    const visibleActions = actions.filter((action) => !normalized || matches(normalized, action.label, action.keywords))

    const customers = useMemo(() => {
        if (normalized.length < 2) return []

        return CUSTOMERS
            .filter((customer) => matches(normalized, customer.company, customer.contact, customer.email))
            .slice(0, 6)
    }, [normalized])

    return (
        <Dialog
            open={commandOpen}
            onOpenChange={(open) => {
                setCommandOpen(open)
                if (!open) setQuery('')
            }}
        >
            <DialogContent
                showCloseButton={false}
                className='top-[12vh] max-w-[calc(100%-1.5rem)] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-xl'
            >
                <DialogTitle className='sr-only'>
                    Pencarian cepat
                </DialogTitle>

                <DialogDescription className='sr-only'>
                    Cari halaman, aksi cepat, atau pelanggan berdasarkan nama.
                </DialogDescription>

                <Command shouldFilter={false} loop className='rounded-none! p-0'>
                    <div className='border-b p-2'>
                        <CommandInput
                            value={query}
                            onValueChange={setQuery}
                            placeholder='Cari halaman, aksi, atau pelanggan'
                            aria-label='Kata kunci pencarian'
                        />
                    </div>

                    <CommandList className='max-h-[min(60vh,440px)] p-1'>
                        <CommandEmpty>
                            Tidak ada hasil untuk &ldquo;{query}&rdquo;.
                        </CommandEmpty>

                        {pages.length > 0 && (
                            <CommandGroup heading='Halaman'>
                                {pages.map((item) => {
                                    const Icon = item.icon

                                    return (
                                        <CommandItem key={item.href} value={`page-${item.href}`} onSelect={() => run(() => router.push(item.href))}>
                                            <Icon className='text-muted-foreground' />
                                            <span className='font-medium'>
                                                {item.label}
                                            </span>
                                            <span className='ml-auto hidden truncate text-xs text-muted-foreground sm:block'>
                                                {item.description}
                                            </span>
                                        </CommandItem>
                                    )
                                })}
                            </CommandGroup>
                        )}

                        {visibleActions.length > 0 && (
                            <>
                                <CommandSeparator className='my-1' />

                                <CommandGroup heading='Aksi cepat'>
                                    {visibleActions.map((action) => {
                                        const Icon = action.icon

                                        return (
                                            <CommandItem key={action.id} value={`action-${action.id}`} onSelect={() => run(action.run)}>
                                                <Icon className='text-muted-foreground' />
                                                {action.label}
                                            </CommandItem>
                                        )
                                    })}
                                </CommandGroup>
                            </>
                        )}

                        {customers.length > 0 && (
                            <>
                                <CommandSeparator className='my-1' />

                                <CommandGroup heading='Pelanggan'>
                                    {customers.map((customer) => (
                                        <CommandItem
                                            key={customer.id}
                                            value={`customer-${customer.id}`}
                                            onSelect={() => run(() => router.push(`/pelanggan?q=${encodeURIComponent(customer.company)}`))}
                                        >
                                            <Building2 className='text-muted-foreground' />
                                            <span className='min-w-0 truncate'>
                                                <span className='font-medium'>
                                                    {customer.company}
                                                </span>
                                                <span className='ml-2 text-xs text-muted-foreground'>
                                                    {customer.contact}
                                                </span>
                                            </span>
                                            <span className='ml-auto shrink-0 text-xs text-muted-foreground'>
                                                {getPlan(customer.plan).name}
                                            </span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </>
                        )}

                        {normalized.length === 1 && (
                            <p className='px-3 py-2 text-xs text-muted-foreground'>
                                Ketik minimal 2 huruf untuk mencari pelanggan.
                            </p>
                        )}
                    </CommandList>

                    <div className='flex flex-wrap items-center gap-x-4 gap-y-1 border-t px-3 py-2 text-[11px] text-muted-foreground'>
                        <span>
                            <kbd className='font-mono'>&uarr;</kbd> <kbd className='font-mono'>&darr;</kbd> pilih
                        </span>
                        <span>
                            <kbd className='font-mono'>Enter</kbd> buka
                        </span>
                        <span>
                            <kbd className='font-mono'>Esc</kbd> tutup
                        </span>
                    </div>
                </Command>
            </DialogContent>
        </Dialog>
    )
}
